/**
 * Lectura de la marca que el callback deja al volver de un acceso correcto.
 *
 * Sirve para distinguir dos situaciones que hoy se veían idénticas — la portada
 * con su botón — y que no lo son en absoluto:
 *
 *   - «Todavía no he entrado». Normal.
 *   - «Entré, el servidor me dio sesión, y aun así vuelvo aquí». Eso significa
 *     que la cookie no llegó de vuelta, y la persona puede pulsar el botón
 *     cuarenta veces sin que cambie nada.
 *
 * La segunda pasó de verdad: diez accesos registrados como correctos en la
 * bitácora, diez regresos a la portada, y ni un aviso.
 */

/** Nombre del parámetro. Debe coincidir con `LOGIN_MARK` de la API. */
export const MARCA_DE_ACCESO = 'login';

export interface Regreso {
  /** Venimos de un acceso que el servidor dio por bueno. */
  veniaDeUnAcceso: boolean;
  /** La misma dirección sin la marca, para no dejarla pegada en la barra. */
  urlLimpia: string;
}

/**
 * Función pura: recibe la dirección y devuelve qué decía y cómo queda sin la
 * marca. No toca el historial — de eso se encarga `recogerMarcaDeAcceso`.
 */
export function leerMarcaDeAcceso(href: string): Regreso {
  const url = new URL(href);
  const veniaDeUnAcceso = url.searchParams.get(MARCA_DE_ACCESO) === 'ok';
  url.searchParams.delete(MARCA_DE_ACCESO);
  return { veniaDeUnAcceso, urlLimpia: `${url.pathname}${url.search}${url.hash}` };
}

let recordado = false;

/**
 * Recoge la marca **antes de montar la aplicación**, no después.
 *
 * El orden no es un detalle de estilo. Las pantallas leen la barra de
 * direcciones para armar sus filtros y se los pasan tal cual a la API, que
 * rechaza los parámetros que no conoce. Al limpiar la marca en un efecto —es
 * decir, ya montado— el primer listado de documentos salió con `login=ok`
 * pegado y la API respondió 400: entrar al portal y aterrizar en un error.
 * Quitándola aquí, la aplicación nunca llega a verla.
 */
export function recogerMarcaDeAcceso(
  ubicacion: Location = window.location,
  historial: History = window.history,
): boolean {
  const { veniaDeUnAcceso, urlLimpia } = leerMarcaDeAcceso(ubicacion.href);
  if (veniaDeUnAcceso) historial.replaceState(null, '', urlLimpia);
  recordado = veniaDeUnAcceso;
  return veniaDeUnAcceso;
}

/** Lo que dijo la marca, ya sin marca en la barra de direcciones. */
export function veniaDeUnAcceso(): boolean {
  return recordado;
}
